import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import {
  Card,
  useAuth,
  useToaster,
  SocialLoginButtons,
} from '../../../shared/ui';
import { createFirebaseEmailUser } from '../../../shared/utils/firebase';
import './RegisterPage.css';

export const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const toaster = useToaster();

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate passwords match
    if (password !== confirmPassword) {
      toaster.error('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      toaster.error('Password must be at least 8 characters long');
      return;
    }

    if (password.length > 128) {
      toaster.error('Password must be less than 128 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      await createFirebaseEmailUser(
        email,
        password,
        `${firstName} ${lastName}`.trim()
      );
      navigate({
        to: '/check-inbox',
        state: { email, reason: 'verify' },
      });
      return;
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/email-already-in-use') {
        toaster.error(
          'An account with this email already exists. Please sign in instead.'
        );
      } else if (code === 'auth/operation-not-allowed') {
        toaster.error(
          'Email/password sign-up is not enabled. Please use Google sign-in or contact support.'
        );
      } else if (code === 'auth/weak-password') {
        toaster.error(
          'Password is too weak. Please use at least 8 characters.'
        );
      } else if (code === 'auth/network-request-failed') {
        toaster.error(
          'Network error. Please check your connection and try again.'
        );
      } else {
        toaster.error(
          (err as { message?: string })?.message ||
            'Registration failed. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginClick = () => {
    navigate({ to: '/login' });
  };

  return (
    <div className="register-page">
      <div className="register-page__container">
        <Card className="register-page__card">
          <>
            <Box className="register-page__header">
              <Typography
                variant="h4"
                className="register-page__title"
                gutterBottom
              >
                Create Account
              </Typography>
              <Typography
                variant="body1"
                className="register-page__subtitle"
                color="text.secondary"
              >
                Join The MIET Club today
              </Typography>
            </Box>

            {/* Social login buttons live outside the email/password form to avoid nested <form> */}
            <SocialLoginButtons
              isRegister
              onSuccess={() => navigate({ to: '/' })}
              onMagicLinkSent={sentEmail =>
                navigate({
                  to: '/check-inbox',
                  state: { email: sentEmail, reason: 'magic-link' },
                })
              }
            />

            <Divider className="register-page__social-divider">
              <Typography variant="body2" color="text.secondary">
                or sign up with email & password
              </Typography>
            </Divider>

            <Box
              component="form"
              onSubmit={handleSubmit}
              className="register-page__form"
              noValidate
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="First Name"
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    autoComplete="given-name"
                  />

                  <TextField
                    label="Last Name"
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    autoComplete="family-name"
                  />
                </Stack>

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="john.doe@example.com"
                  required
                  fullWidth
                  disabled={isSubmitting}
                  variant="outlined"
                  autoComplete="email"
                />

                <Box>
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    autoComplete="new-password"
                  />
                  <Alert
                    severity="info"
                    className="register-page__password-hint"
                  >
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <li>Minimum 8 characters</li>
                      <li>Maximum 128 characters</li>
                    </Box>
                  </Alert>
                </Box>

                <TextField
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  fullWidth
                  disabled={isSubmitting}
                  variant="outlined"
                  autoComplete="new-password"
                />
              </Stack>

              <Box className="register-page__submit">
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating account...' : 'Sign Up'}
                </Button>
              </Box>
            </Box>

            <Box className="register-page__footer">
              <Typography
                variant="body2"
                className="register-page__footer-text"
              >
                Already have an account?{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleLoginClick}
                  className="register-page__signin-btn"
                >
                  Sign In
                </Button>
              </Typography>
            </Box>
          </>
        </Card>
      </div>
    </div>
  );
};
